import React, { useState } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { Home, Users, BarChart3, Plus, MoreHorizontal, ScrollText, HelpCircle, Settings, LogOut, LayoutDashboard, UserCog, Image } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { BUILD } from "@/lib/build";

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [moreOpen, setMoreOpen] = useState(false);
  const isAdmin = user?.role !== "supervisor";
  const first = (user?.name || "").split(" ")[0];
  const initial = (first || "U")[0].toUpperCase();

  const desktopNav = [
    { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
    { to: "/new", label: "New Entry", icon: Plus },
    { to: "/workers", label: "Workers", icon: Users },
    { to: "/mylog", label: "My Log", icon: ScrollText },
    { to: "/reports", label: "Reports", icon: BarChart3 },
    { to: "/photos", label: "Photos", icon: Image },
  ];
  if (isAdmin) desktopNav.push({ to: "/admin", label: "Admin", icon: Settings });

  const moreLinks = [
    { to: "/mylog", label: "My Log", icon: ScrollText },
    { to: "/reports", label: "Reports", icon: BarChart3 },
    { to: "/photos", label: "Photos", icon: Image },
    { to: "/account", label: "My Account", icon: UserCog },
    { to: "/faq", label: "FAQ", icon: HelpCircle },
    ...(isAdmin ? [{ to: "/admin", label: "Admin", icon: Settings }] : []),
  ];

  return (
    <div className="min-h-screen flex flex-col md:flex-row">
      {/* Desktop sidebar */}
      <aside className="hidden md:flex md:flex-col w-64 btn-grad text-white sticky top-0 h-screen p-5">
        <div className="mb-8 flex items-center gap-3">
          <img src="/mark-white.png" alt="Ready2Work AU" className="h-11 w-11 object-contain shrink-0" />
          <div className="min-w-0">
            <div className="font-heading font-extrabold text-2xl tracking-tight leading-none">Ready2Work</div>
            <div className="text-white/80 text-xs font-medium mt-1 truncate">{user?.org_name}</div>
          </div>
        </div>
        <nav className="flex-1 space-y-1">
          {desktopNav.map((n) => (
            <NavLink key={n.to} to={n.to} data-testid={`nav-${n.label.toLowerCase().replace(/ /g, "-")}`}
              className={({ isActive }) => `flex items-center gap-3 px-4 py-3 rounded-xl font-medium transition-colors ${isActive ? "bg-white/20 text-white" : "text-white/85 hover:bg-white/10"}`}>
              <n.icon strokeWidth={2.5} size={20} /> {n.label}
            </NavLink>
          ))}
          <NavLink to="/faq" data-testid="nav-faq" className={({ isActive }) => `flex items-center gap-3 px-4 py-3 rounded-xl font-medium transition-colors ${isActive ? "bg-white/20 text-white" : "text-white/85 hover:bg-white/10"}`}>
            <HelpCircle strokeWidth={2.5} size={20} /> FAQ
          </NavLink>
          <NavLink to="/account" data-testid="nav-my-account" className={({ isActive }) => `flex items-center gap-3 px-4 py-3 rounded-xl font-medium transition-colors ${isActive ? "bg-white/20 text-white" : "text-white/85 hover:bg-white/10"}`}>
            <UserCog strokeWidth={2.5} size={20} /> My Account
          </NavLink>
        </nav>
        <div className="border-t border-white/20 pt-4">
          <div className="px-2 mb-3"><div className="font-semibold">{user?.name}</div><div className="text-xs text-white/70 capitalize">{user?.role}</div></div>
          <button data-testid="logout-btn" onClick={logout} className="flex items-center gap-3 px-4 py-2.5 rounded-xl text-white/85 hover:bg-white/10 w-full font-medium">
            <LogOut strokeWidth={2.5} size={20} /> Sign out
          </button>
          <div data-testid="build-stamp" className="px-2 mt-3 text-[10px] text-white/50 font-num">{BUILD}</div>
        </div>
      </aside>

      {/* Mobile top greeting bar */}
      <header className="md:hidden sticky top-0 z-20 bg-white/95 backdrop-blur border-b border-slate-100 px-4 h-16 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full btn-grad grid place-items-center text-white font-heading font-bold">{initial}</div>
          <div>
            <div className="font-heading font-bold text-slate-800 leading-tight">G'day, {first} <span className="align-middle">👋</span></div>
            <div className="text-xs text-slate-400 capitalize">{user?.org_name} · {user?.role}</div>
          </div>
        </div>
        <button data-testid="logout-btn-mobile" onClick={logout} className="text-slate-400"><LogOut strokeWidth={2.5} size={20} /></button>
      </header>

      <main className="flex-1 pb-28 md:pb-8 max-w-5xl w-full mx-auto">{children}</main>

      {/* Mobile bottom nav with center FAB */}
      <nav className="md:hidden fixed bottom-0 inset-x-0 z-20 bg-white border-t border-slate-100 h-[68px] flex items-end justify-around px-2">
        <BottomItem to="/dashboard" label="Home" icon={Home} active={location.pathname === "/dashboard"} testid="bottomnav-home" />
        <BottomItem to="/workers" label="Workers" icon={Users} active={location.pathname === "/workers"} testid="bottomnav-workers" />
        <button data-testid="fab-new-entry" onClick={() => navigate("/new")} aria-label="New entry"
          className="btn-grad text-white w-14 h-14 rounded-full grid place-items-center -mt-6 shadow-lg shadow-teal-500/30 active:scale-95 transition-transform">
          <Plus strokeWidth={3} size={26} />
        </button>
        <BottomItem to="/reports" label="Reports" icon={BarChart3} active={location.pathname === "/reports"} testid="bottomnav-reports" />
        <Sheet open={moreOpen} onOpenChange={setMoreOpen}>
          <SheetTrigger asChild>
            <button data-testid="bottomnav-more" className={`flex flex-col items-center justify-center gap-0.5 text-[11px] font-medium pb-2 flex-1 ${moreOpen ? "text-teal-600" : "text-slate-400"}`}>
              <MoreHorizontal strokeWidth={2.5} size={24} /> More
            </button>
          </SheetTrigger>
          <SheetContent side="bottom" className="rounded-t-3xl">
            <SheetHeader><SheetTitle className="text-left font-heading text-slate-800">Menu</SheetTitle></SheetHeader>
            <div className="grid grid-cols-2 gap-3 py-4">
              {moreLinks.map((l) => (
                <button key={l.to} data-testid={`more-${l.label.toLowerCase()}`} onClick={() => { setMoreOpen(false); navigate(l.to); }}
                  className="flex items-center gap-3 bg-teal-50 rounded-2xl p-4 text-teal-800 font-medium active:scale-[0.98] transition-transform">
                  <l.icon strokeWidth={2.5} size={20} /> {l.label}
                </button>
              ))}
              <button data-testid="more-signout" onClick={logout} className="flex items-center gap-3 bg-rose-50 rounded-2xl p-4 text-rose-600 font-medium col-span-2 active:scale-[0.98] transition-transform">
                <LogOut strokeWidth={2.5} size={20} /> Sign out
              </button>
              <div data-testid="build-stamp" className="col-span-2 text-center text-[11px] text-slate-300 font-num pt-1">{BUILD}</div>
            </div>
          </SheetContent>
        </Sheet>
      </nav>
    </div>
  );
}

function BottomItem({ to, label, icon: Icon, active, testid }) {
  return (
    <NavLink to={to} data-testid={testid} className={`flex flex-col items-center justify-center gap-0.5 text-[11px] font-medium pb-2 flex-1 ${active ? "text-teal-600" : "text-slate-400"}`}>
      <Icon strokeWidth={2.5} size={24} /> {label}
    </NavLink>
  );
}
